<template>
  <div>
    <h1 class="outline--text">Suppressed Emails</h1>
    <br>

    <v-card>
      <v-row class="mb-4" dense>
        <v-card-title>
          <v-text-field
            v-model="filters.email"
            label="Email"
            clearable
          ></v-text-field>
        </v-card-title>

        <v-card-title>
          <v-select
            v-model="filters.isActivelySuppressed"
            :items="[
              { text: 'Yes', value: 1 },
              { text: 'No', value: 0 },
              { text: 'All', value: null }
            ]"
            label="AWS Banned"
            clearable
          ></v-select>
        </v-card-title>

        <v-card-title>
          <v-btn color="primary" class="mr-2" @click="fetchSuppressedEmails">
            Apply Filters
          </v-btn>
          <v-btn text @click="resetFilters">
            Reset
          </v-btn>
        </v-card-title>
      </v-row>

      <!-- Data loader -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="my-3"
      ></v-progress-linear>

      <v-data-table
        :headers="headers"
        :items="suppressedEmails"
        :search="search"
        :loading="isLoading"
        :no-data-text="isLoading ? 'Loading items...' : 'No suppressed emails found'"
        class="elevation-1"
      >
        <template v-slot:item.is_actively_suppressed="{ item }">
          <v-chip
            :color="item.is_actively_suppressed ? 'red' : 'green'"
            dark
          >
            {{ item.is_actively_suppressed ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ item.created_at }}
        </template>

        <template v-slot:item.updated_at="{ item }">
          {{ item.updated_at }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            color="blue"
            class="mr-2"
            @click="openUpdateDialog(item)"
            title="Update"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            color="red"
            class="mr-2"
            @click="deleteSuppressedEmail(item.email)"
            title="Delete"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar
      v-model="showError"
      color="error"
      timeout="5000"
    >
      {{ error }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>Update Suppressed Email</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editedData.comment"
            label="Comment"
            clearable
          ></v-text-field>
          <v-select
            v-model="editedData.is_actively_suppressed"
            :items="[
              { text: 'Yes', value: 1 },
              { text: 'No', value: 0 }
            ]"
            label="AWS Banned"
            clearable
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitUpdate">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_SUPPRESSED_EMAILS,
  DELETE_SUPPRESSED_EMAIL,
  UPDATE_SUPPRESSED_EMAIL,
} from "@/store/modules/suppressed_emails/actions";

export default {
  name: "SuppressedEmailPage",
  data() {
    return {
      search: "",
      showError: false,
      filters: {
        email: "",
        isActivelySuppressed: 1,
      },
      headers: [
        { text: "User", value: "user" },
        { text: "Email", value: "email" },
        { text: "Reason", value: "suppressed_reason" },
        { text: "AWS Banned", value: "is_actively_suppressed" },
        { text: "Comment", value: "comment" },
        { text: "Bounce Letter", value: "bounce_letter_name" },
        { text: "Number of Tries", value: "number_of_tries" },
        { text: "Created At", value: "created_at" },
        { text: "Updated At", value: "updated_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      dialog: false,
      editedEmail: null,
      editedData: {
        comment: "",
        is_actively_suppressed: 1,
      },
    };
  },
  computed: {
    ...mapGetters("suppressedEmails", ["suppressedEmails", "isLoading", "error"]),
  },
  methods: {
    fetchSuppressedEmails() {
      const params = {
        email: this.filters.email || undefined,
        is_actively_suppressed: this.filters.isActivelySuppressed,
        search: this.search || undefined,
      };

      // Remove undefined/null/empty string keys
      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      this.$store
        .dispatch(`suppressedEmails/${FETCH_SUPPRESSED_EMAILS}`, { params })
        .catch((error) => {
          console.error("Error fetching suppressed emails:", error);
          this.showError = true;
        });
    },
    resetFilters() {
      this.filters.email = "";
      this.filters.isActivelySuppressed = null;
      this.search = "";
      this.fetchSuppressedEmails();
    },
    deleteSuppressedEmail(email) {
      if (confirm(`Are you sure you want to delete "${email}"?`)) {
        this.$store
          .dispatch(`suppressedEmails/${DELETE_SUPPRESSED_EMAIL}`, email)
          .then(() => {
            this.fetchSuppressedEmails();
          })
          .catch((error) => {
            console.error("Error deleting suppressed email:", error);
            this.showError = true;
          });
      }
    },
    openUpdateDialog(item) {
      this.editedEmail = item.email;
      this.editedData.comment = item.comment;
      this.editedData.is_actively_suppressed = item.is_actively_suppressed;
      this.dialog = true;
    },
    submitUpdate() {
      this.$store
        .dispatch(`suppressedEmails/${UPDATE_SUPPRESSED_EMAIL}`, {
          email: this.editedEmail,
          data: this.editedData,
        })
        .then(() => {
          this.dialog = false;
          this.fetchSuppressedEmails(); // refresh
        })
        .catch((err) => {
          console.error("Error updating email:", err);
          this.showError = true;
        });
    },
  },
  mounted() {
    this.fetchSuppressedEmails();
  },
  watch: {
    error(newVal) {
      if (newVal) {
        this.showError = true;
      }
    },
  },
};
</script>
